import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  Paper,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Header from './Header';
import { useLanguage } from '../context/LanguageContext';
import LanguageModal from './LanguageModal';

const VolunteerRecommendation = ({ userEmail, onBack, onProfileClick }) => {
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const { t, currentLanguage, getLanguageName, isRtl } = useLanguage();
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    recommenderName: '',
    recommenderEmail: userEmail || '',
    mentorName: '',
    yearsKnown: '',
    relationship: '',
    abilityWithChildren: '',
    exampleWithChildren: '',
    patienceRating: '',
    harshDiscipline: '',
    boundarySetting: '',
    culturalRespect: '',
    abilityWithAdults: '',
    exampleWithAdults: '',
    supportRating: '',
    concernExample: '',
    recentStressors: '',
    additionalComments: ''
  });

  // Rating options
  const ratingOptions = ['Above Satisfactory', 'Satisfactory', 'Below Satisfactory'];
  const yearsKnownOptions = ['<1 year', '1-2 years', '2-3 years', '3+ years'];

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch("https://me40bh7o7b.execute-api.us-east-1.amazonaws.com/prod/recommendation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          user_email: userEmail,
          formData
        })
      });

      if (!response.ok) throw new Error("Submission failed");

      alert('Thank you for your recommendation!');
      onBack();
    } catch (err) {
      console.error("Submission error:", err);
      alert('There was an error submitting your recommendation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#F5F5F5', direction: isRtl ? 'rtl' : 'ltr' }}>
      <Header
        showProfileIcon={true}
        onProfileClick={onProfileClick}
        onLanguageClick={() => setShowLanguageModal(true)}
        languageLabel={getLanguageName(currentLanguage)}
      />

      <LanguageModal
        open={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
      />

      <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <Card sx={{
          width: '100%',
          maxWidth: 800,
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 2,
          boxShadow: 3,
          position: 'relative'
        }}>
          <IconButton
            sx={{
              position: 'absolute',
              left: isRtl ? 8 : 'auto',
              right: isRtl ? 'auto' : 8,
              top: 8,
              zIndex: 2,
              color: '#fff'
            }}
            onClick={onBack}
          >
            <CloseIcon />
          </IconButton>

          <CardHeader
            title={<Typography variant="h6" sx={{ color: '#fff' }}>Volunteer Recommendation</Typography>}
            sx={{
              backgroundColor: '#E27A58',
              position: 'relative'
            }}
          />

          <CardContent sx={{
            flexGrow: 1,
            overflowY: 'auto',
            px: 3,
          }}>
            <form onSubmit={handleSubmit}>
              {/* Recommender Information */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle1" gutterBottom>Your Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Name (First and Last)"
                      name="recommenderName"
                      value={formData.recommenderName}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="recommenderEmail"
                      type="email"
                      value={formData.recommenderEmail}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Mentor Information */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle1" gutterBottom>Prospective Mentor Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Potential Mentor Name (First and Last)"
                      name="mentorName"
                      value={formData.mentorName}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>How long have you known the prospective mentor?</InputLabel>
                      <Select
                        name="yearsKnown"
                        value={formData.yearsKnown}
                        onChange={handleChange}
                        label="How long have you known the prospective mentor?"
                      >
                        {yearsKnownOptions.map(option => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="What is your relationship to the prospective mentor?"
                      name="relationship"
                      value={formData.relationship}
                      onChange={handleChange}
                      multiline
                      rows={2}
                      required
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Working with Children */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle1" gutterBottom>Working with Children</Typography>

                <FormControl fullWidth required sx={{ mb: 2 }}>
                  <InputLabel>Ability to work with and relate to children</InputLabel>
                  <Select
                    name="abilityWithChildren"
                    value={formData.abilityWithChildren}
                    onChange={handleChange}
                    label="Ability to work with and relate to children"
                  >
                    {ratingOptions.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Can you give me an example of how the prospective mentor relates to children?"
                  name="exampleWithChildren"
                  value={formData.exampleWithChildren}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  required
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth required sx={{ mb: 2 }}>
                  <InputLabel>Ability to be patient</InputLabel>
                  <Select
                    name="patienceRating"
                    value={formData.patienceRating}
                    onChange={handleChange}
                    label="Ability to be patient"
                  >
                    {ratingOptions.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Have you ever known the prospective mentor to use harsh or abusive discipline with a student?"
                  name="harshDiscipline"
                  value={formData.harshDiscipline}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  required
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Do you feel that the prospective mentor is able to set boundaries with a student? Why or why not?"
                  name="boundarySetting"
                  value={formData.boundarySetting}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  required
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Do you feel that the prospective mentor is able to respect people from different ethnic groups and religious beliefs? Why or why not?"
                  name="culturalRespect"
                  value={formData.culturalRespect}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  required
                />
              </Paper>

              {/* Working with Adults */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle1" gutterBottom>Working with Adults</Typography>

                <FormControl fullWidth required sx={{ mb: 2 }}>
                  <InputLabel>Ability to relate with adults</InputLabel>
                  <Select
                    name="abilityWithAdults"
                    value={formData.abilityWithAdults}
                    onChange={handleChange}
                    label="Ability to relate with adults"
                  >
                    {ratingOptions.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Can you give me an example of how the prospective mentor relates with adults?"
                  name="exampleWithAdults"
                  value={formData.exampleWithAdults}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  required
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth required sx={{ mb: 2 }}>
                  <InputLabel>Ability to be supportive and understanding</InputLabel>
                  <Select
                    name="supportRating"
                    value={formData.supportRating}
                    onChange={handleChange}
                    label="Ability to be supportive and understanding"
                  >
                    {ratingOptions.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Can you think of a time when the prospective mentor was able to show genuine concern for another person who needed comfort? Tell about that time."
                  name="concernExample"
                  value={formData.concernExample}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  required
                  sx={{ mb: 2 }}
                />
              </Paper>

              {/* Additional Information */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid rgba(0, 0, 0, 0.12)' }}>
                <Typography variant="subtitle1" gutterBottom>Additional Information</Typography>

                <TextField
                  fullWidth
                  label="Are you aware of any significant stressors recently experienced by the prospective mentor?"
                  helperText="(An example would be a death in the family, divorce or a financial crisis.)"
                  name="recentStressors"
                  value={formData.recentStressors}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  required
                  sx={{ mb: 2 }}
                  FormHelperTextProps={{
                    sx: {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word'
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="Do you have any additional comments, concerns, or questions?"
                  name="additionalComments"
                  value={formData.additionalComments}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  required
                />
              </Paper>

              {/* Submit Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  sx={{ bgcolor: '#E27A58', '&:hover': { bgcolor: '#d2694a' } }}
                >
                  {submitting ? 'Submitting...' : 'Submit Reference'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ p: 2, bgcolor: '#1EAEB8', color: '#fff', textAlign: 'center', mt: 'auto' }}>
        <Typography variant="body2">© {new Date().getFullYear()} Refugee Resources, Inc.</Typography>
      </Box>
    </Box>
  );
};

export default VolunteerRecommendation;
